import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { PageSection, useToast } from '@radiology/ui';
import { money, useCreateRequest, type NewRequestInput } from './api';
import { useSession } from '../../auth/SessionContext';

const EMPTY: NewRequestInput = {
  serial_no: '',
  description: '',
  cat_no: '',
  existing_item: 'No',
  unit_of_order: '',
  quantity: 1,
  unit_price: 0,
  justification: '',
  section_head_signature: '',
  department: '',
};

/**
 * Raise a purchase request.
 *
 * The legacy form had eleven fields and validated none of them beyond `required`
 * — a quantity of "abc" reached the database as 0, and `total` was computed in
 * the browser and sent, so it could disagree with quantity × unit price.
 *
 * Here the total is derived at submit time and is not in the `authenticated`
 * UPDATE grant, so it cannot be edited afterwards either. The requester is taken
 * from the session, and the INSERT policy independently requires it to be the
 * caller — this form cannot file a request in someone else's name.
 */
export function NewRequestForm({ onCreated }: { onCreated?: () => void }) {
  const session = useSession();
  const create = useCreateRequest();
  const toast = useToast();
  const [form, setForm] = useState<NewRequestInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof NewRequestInput>(key: K, value: NewRequestInput[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const total = Number(form.quantity) * Number(form.unit_price);

  const submit = () => {
    const missing =
      !form.serial_no.trim() ||
      !form.description.trim() ||
      !form.justification.trim() ||
      !form.section_head_signature.trim();

    if (missing) {
      setError('Item, serial number, justification and the section head are all required.');
      return;
    }
    if (!Number.isFinite(form.quantity) || form.quantity < 1) {
      setError('Quantity must be at least 1.');
      return;
    }
    if (!Number.isFinite(form.unit_price) || form.unit_price <= 0) {
      setError('Unit price must be greater than zero.');
      return;
    }

    setError(null);
    create.mutate(
      {
        ...form,
        creator: {
          username: session.user.username,
          hospital: session.context.hospital,
          section: session.context.section,
        },
      },
      {
        onSuccess: () => {
          toast.success('Request raised. It is now waiting for an approver.');
          setForm(EMPTY);
          onCreated?.();
        },
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  return (
    <PageSection
      title="Raise a request"
      description="It goes to an approver at your hospital, then to the chief approver."
    >
      <Stack gap={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              label="Item"
              fullWidth
              required
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Serial number"
              fullWidth
              required
              value={form.serial_no}
              onChange={e => set('serial_no', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Catalogue number"
              fullWidth
              value={form.cat_no}
              onChange={e => set('cat_no', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              label="Already in use here"
              fullWidth
              value={form.existing_item}
              onChange={e => set('existing_item', e.target.value as 'Yes' | 'No')}
            >
              <MenuItem value="No">No — new to us</MenuItem>
              <MenuItem value="Yes">Yes — we already have one</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Unit of order"
              placeholder="box, each, pack…"
              fullWidth
              value={form.unit_of_order}
              onChange={e => set('unit_of_order', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Department"
              fullWidth
              value={form.department}
              onChange={e => set('department', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Quantity"
              type="number"
              fullWidth
              required
              inputProps={{ min: 1, step: 1 }}
              value={form.quantity}
              onChange={e => set('quantity', Number(e.target.value))}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Unit price (SAR)"
              type="number"
              fullWidth
              required
              inputProps={{ min: 0, step: '0.01' }}
              value={form.unit_price}
              onChange={e => set('unit_price', Number(e.target.value))}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            {/* Derived, never entered — the legacy form let these disagree. */}
            <TextField
              label="Total"
              fullWidth
              disabled
              value={Number.isFinite(total) ? money(total) : '—'}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Justification"
              fullWidth
              required
              multiline
              minRows={3}
              helperText="Why it is needed. The approver reads this and nothing else."
              value={form.justification}
              onChange={e => set('justification', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Section head"
              fullWidth
              required
              helperText="Who signed off on this in your section."
              value={form.section_head_signature}
              onChange={e => set('section_head_signature', e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction="row" gap={1.5} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary">
            Raised as {session.user.username}
            {session.context.hospital ? ` · ${session.context.hospital}` : ''}
          </Typography>
          <Button variant="contained" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Sending…' : 'Raise request'}
          </Button>
        </Stack>
      </Stack>
    </PageSection>
  );
}
